import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { RootState } from 'store';
import getPath from 'helpers/path';
import { setCurrentCurrency, setRefreshId } from 'actions/home';
import { useTranslation } from 'hooks';
import apiUser from 'api/user';
import landingState from 'constants/landingState';
import PageTemplate from 'components/pageTemplate';
import Button from 'components/ui/button';
import {
  TotalSummaries,
  QuickActions,
  OverviewGraphs,
  MainBanner,
} from './modules';
import { CurrencySelect } from './components/CurrencySelect';
import Loader from 'components/ui/loader';
import { ComponentRegistry } from 'runtime/registry/components/ComponentRegistry';

const Home: FC = () => {
  const props = ComponentRegistry.getProps('Home');

  const getTranslate = useTranslation();
  const dispatch = useDispatch();
  const { user, currentCurrency } = useSelector((state: RootState) => ({
    user: state.user,
    currentCurrency: state.home.currentCurrency,
  }));

  useEffect(() => {
    if (!currentCurrency) {
      fetchSelectedCurrency();
    }
  }, [currentCurrency]);

  const fetchSelectedCurrency = async () => {
    try {
      const { homePageCurrency } = await apiUser.getConfig({
        param: 'homePageCurrency',
      });
      dispatch(setCurrentCurrency({ currency: homePageCurrency }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleRefresh = () => {
    dispatch(setRefreshId({ id: uuid() }));
  };

  if (user.landingState !== landingState.default) {
    return <Redirect to={getPath('/')} />;
  }

  if (!currentCurrency) {
    return <Loader />;
  }

  return (
    <PageTemplate.Main>
      <PageTemplate.Container customClass='home-page overflow-x-hidden overflow-y-auto p-0'>
        <div className='home-page__banner'>
          <MainBanner />
        </div>

        <div className='max-tablet:pt-6 home-page__content p-4 pt-10'>
          <main className='max-desktop-s:max-w-[834px] home-page__inner mx-auto w-full max-w-[1200px]'>
            <div className='max-tablet:mb-6 home-page__header mb-12 flex items-start justify-between gap-2'>

              <div className='max-mobile-s:w-full max-mobile-s:justify-between home-page__controls flex items-center gap-3'>
                <Button
                  id='refresh'
                  status='light'
                  icon='im-Refill'
                  iconSize={14}
                  iconPosition='left'
                  text={getTranslate('common.refresh.button')}
                  onClick={handleRefresh}
                />
                <div className='home-page__currency-select'>
                  <CurrencySelect filteredCurrencyList={props?.currencyList} />
                </div>
              </div>
            </div>
            <div className='max-desktop-s:gap-6 max-tablet:gap-4 home-page__sections flex flex-col gap-8'>
              <div className='home-page__section home-page__section--summaries'>
                <TotalSummaries />
              </div>
              <div className='home-page__section home-page__section--actions'>
                <QuickActions />
              </div>
              <div className='home-page__section home-page__section--graphs'>
                <OverviewGraphs />
              </div>
            </div>
          </main>
        </div>
      </PageTemplate.Container>
    </PageTemplate.Main>
  );
};

export default Home;
